import {
  ClientApplicationForTableFragment,
  useClientApplicationsForTableQuery,
} from "@graphql/client-applications/ClientApplications.g";
import { ClientResourceGroupFragment } from "@graphql/client-resource-groups/ClientResourceGroups.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import { keepPreviousData } from "@tanstack/react-query";
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  ClientApplicationsTableConfig,
  renderBusinessOwner,
  renderCategory,
  renderCreatedAt,
  renderManagedBy,
  renderNotes,
  renderNotifications,
  renderProductDescription,
  renderProductName,
  renderReviewState,
  renderSupportedBy,
  renderUpdatedAt,
  renderUsedBy,
  renderVendorDescription,
  renderVendorName,
} from "./ClientApplicationsTableConfig";
import { ClientResourceGroupReviewPanel } from "./ClientResourceGroupReviewPanel/ClientResourceGroupReviewPanel";
import { ClientResourceInfoPanel } from "./ClientResourceInfoPanel/ClientResourceInfoPanel";
import { ClientApplicationTableColumns } from "./UI/ClientApplicationsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;
const PRODUCT_NAME_SEARCH_DEBOUNCE_TIME = 300;

const columnHelper = createColumnHelper<ClientApplicationForTableFragment>();

type Props = {
  clientId: string;
  initialPageIndex?: number;
  search?: string;
  reviewState?: TenantedClientApplicationReviewStateChoices[];
  managedBy?: TenantedClientApplicationManagedByChoices[];
  supportedBy?: TenantedClientApplicationSupportedByChoices[];
  businessOwnerId?: string;
  productNameSearch?: string;
  parentCategoryId?: string;
  createdAt_Gte?: string;
  createdAt_Lte?: string;
};

export const ClientApplicationsTable = ({
  clientId,
  initialPageIndex = 0,
  search,
  reviewState,
  managedBy,
  supportedBy,
  businessOwnerId,
  productNameSearch,
  parentCategoryId,
  createdAt_Gte,
  createdAt_Lte,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]); // Add sorting state

  // Custom sorting handler that toggles between ASC and DESC only (no unsorted state)
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      setSorting((prevSorting) => {
        const newSorting =
          typeof updater === "function" ? updater(prevSorting) : updater;

        // If trying to clear sorting (empty array), default to descending on the first sortable column
        if (newSorting.length === 0 && prevSorting.length > 0) {
          // Toggle to opposite direction instead of clearing
          return [
            {
              id: prevSorting[0].id,
              desc: !prevSorting[0].desc,
            },
          ];
        }

        return newSorting;
      });
    },
    [],
  );
  const [selectedApplicationForReview, setSelectedApplicationForReview] =
    useState<ClientApplicationForTableFragment | null>(null);
  const [
    selectedApplicationForResourceInfo,
    setSelectedApplicationForResourceInfo,
  ] = useState<ClientApplicationForTableFragment | null>(null);
  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);
  const debouncedProductNameSearch = useDebounce(
    productNameSearch,
    PRODUCT_NAME_SEARCH_DEBOUNCE_TIME,
  );

  // Use client applications query with primary resources only
  const clientApplicationsQuery = useClientApplicationsForTableQuery(
    {
      clientId,
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      orderBy: ConfigurableTableSystem.generateSortParameter(
        ClientApplicationsTableConfig.columns,
        sorting,
      ),
      reviewState_In: reviewState,
      managedBy_In: managedBy,
      supportedBy_In: supportedBy,
      businessOwner_In: businessOwnerId ? [businessOwnerId] : undefined,
      resource_ProductName_Icontains: debouncedProductNameSearch || undefined,
      parentCategoryId: parentCategoryId || undefined,
      createdAt_Gte: createdAt_Gte || undefined,
      createdAt_Lte: createdAt_Lte || undefined,
    },
    {
      // Keep previous data while fetching to avoid showing "no results"
      placeholderData: keepPreviousData,
    },
  );
  const refetchClientApplications = clientApplicationsQuery.refetch;
  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isClientApplicationsLoading = clientApplicationsQuery.isLoading;
  const isClientApplicationsFetching = clientApplicationsQuery.isFetching;

  // Extract applications from query response
  const data = useMemo(() => {
    const edges = clientApplicationsQuery.data?.clientApplications?.edges ?? [];
    return edges
      .map((edge) => edge?.node)
      .filter((app): app is ClientApplicationForTableFragment => app !== null);
  }, [clientApplicationsQuery.data]);

  const totalCount =
    clientApplicationsQuery.data?.clientApplications?.totalCount ?? 0;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [
    debouncedSearch,
    reviewState,
    managedBy,
    supportedBy,
    businessOwnerId,
    debouncedProductNameSearch,
    parentCategoryId,
    createdAt_Gte,
    createdAt_Lte,
  ]);

  const handleApplicationDashboardClick = useCallback(
    (applicationId: string) => {
      // Navigate to dashboard with the application ID
      navigate(
        routes.portal.clients.client.applications.application.dashboard.$buildPath(
          {
            params: {
              clientId,
              applicationId,
            },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const handleUsersClick = useCallback(
    (applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.users.$buildPath({
          params: {
            clientId,
            applicationId,
          },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const handleReviewClick = useCallback(
    (application: ClientApplicationForTableFragment) => {
      setSelectedApplicationForReview(application);
    },
    [],
  );

  const handleResourceInfoClick = useCallback(
    (application: ClientApplicationForTableFragment) => {
      setSelectedApplicationForResourceInfo(application);
    },
    [],
  );

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedApplicationForReview(null);
  }, []);

  const handleCloseResourceInfoPanel = useCallback(() => {
    setSelectedApplicationForResourceInfo(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    refetchClientApplications();
  }, [refetchClientApplications]);

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...ClientApplicationsTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientApplicationsTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (
                value: unknown,
                row: ClientApplicationForTableFragment,
              ) => {
                const columnKey = key as ClientApplicationTableColumns;
                // Get aggregated stats from resourceGroupApplications
                const stats = row.resourceGroupApplications?.stats;

                // Map to the appropriate render function
                switch (columnKey) {
                  case ClientApplicationTableColumns.productName:
                    // Use the standard product name renderer
                    return renderProductName(
                      row.resource?.productName,
                      row,
                      handleApplicationDashboardClick,
                    );
                  case ClientApplicationTableColumns.reviewState:
                    // Show application's review state
                    return renderReviewState(row.reviewState);
                  case ClientApplicationTableColumns.managedBy:
                    // Show application's managed by
                    return renderManagedBy(row.managedBy);
                  case ClientApplicationTableColumns.supportedBy:
                    // Show application's supported by
                    return renderSupportedBy(row.supportedBy);
                  case ClientApplicationTableColumns.usedBy:
                    // Use aggregated total employees from stats
                    return renderUsedBy(stats?.totalEmployees ?? 0);
                  case ClientApplicationTableColumns.category: {
                    // Get parent category name, fallback to category name
                    const parentName =
                      row.resource?.publicResource?.category?.parent?.name;
                    const categoryName =
                      row.resource?.publicResource?.category?.name;
                    return renderCategory(parentName || categoryName);
                  }
                  case ClientApplicationTableColumns.vendorName:
                    return renderVendorName(row.resource?.vendorName);
                  case ClientApplicationTableColumns.businessOwner:
                    return renderBusinessOwner(row.businessOwner?.name || "—");
                  case ClientApplicationTableColumns.createdAt:
                    return renderCreatedAt(row.createdAt);
                  case ClientApplicationTableColumns.updatedAt:
                    return renderUpdatedAt(row.updatedAt);
                  case ClientApplicationTableColumns.notes:
                    // Show notes value
                    return renderNotes(row.notes);
                  case ClientApplicationTableColumns.notifications:
                    // Use aggregated total notifications from stats
                    return renderNotifications(stats?.totalNotifications ?? 0);
                  case ClientApplicationTableColumns.productDescription:
                    return renderProductDescription(
                      row.resource?.productDescription,
                    );
                  case ClientApplicationTableColumns.vendorDescription:
                    return renderVendorDescription(
                      row.resource?.vendorDescription,
                    );
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof ClientApplicationsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientApplicationTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings, handleApplicationDashboardClick]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<ClientApplicationForTableFragment, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Dashboard"
                  icon="faGrip"
                  onSelect={() =>
                    handleApplicationDashboardClick(props.row.original.id)
                  }
                />
                <Dropdown.Option
                  label="Review"
                  icon="faPencil"
                  onSelect={() => handleReviewClick(props.row.original)}
                />
                <Dropdown.Option
                  label="Resource Info"
                  icon="faCircleInfo"
                  onSelect={() => handleResourceInfoClick(props.row.original)}
                />
                <Dropdown.Option
                  label="Users"
                  icon="faUser"
                  onSelect={() => handleUsersClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleApplicationDashboardClick,
    handleReviewClick,
    handleResourceInfoClick,
    handleUsersClick,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<ClientApplicationForTableFragment, string>
        columns={allColumnsWithActions}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={isClientApplicationsLoading}
        isFetching={isClientApplicationsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={columnSettings}
        manualSorting={true}
      />

      <ClientResourceGroupReviewPanel
        resourceGroup={
          selectedApplicationForReview as unknown as ClientResourceGroupFragment
        }
        clientId={clientId}
        isOpen={!!selectedApplicationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />

      <ClientResourceInfoPanel
        resourceGroup={
          selectedApplicationForResourceInfo as unknown as ClientResourceGroupFragment
        }
        isOpen={!!selectedApplicationForResourceInfo}
        onClose={handleCloseResourceInfoPanel}
      />
    </>
  );
};
